import { useAuth } from '../../state/auth'
import { apiFetch } from '../../lib/api'
import toast from 'react-hot-toast'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IMaskInput } from 'react-imask'
import { useEffect, useState } from 'react'
import { ConfirmModal } from '../ConfirmModal'

type CompanyUser = { userId: string; email: string; userType?: 'usuario' | 'cliente'; role: 'admin' | 'rh' }

type TaxBracket = {
  from: number
  to: number
  rate: number
  deduction: number
}

type TaxConfig = {
  companyId: string
  inssBrackets: TaxBracket[]
  inssCeilingEmployee: number
  inssCeilingEmployer: number
  familyAllowanceBase: number
  familyAllowanceValue: number
  irrfBrackets: TaxBracket[]
  irrfDependentDeduction: number
  irrfReductionEnabled: boolean
  irrfReductionExemptLimit: number
  irrfReductionMaxAmount: number
  irrfReductionRangeFrom: number
  irrfReductionRangeTo: number
  irrfReductionFormulaBase: number
  irrfReductionFormulaRate: number
}

export function SettingsPage() {
  const { me, refreshMe, selectedCompanyId } = useAuth()
  const qc = useQueryClient()
  const [minWageStr, setMinWageStr] = useState<string>('')
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserType, setNewUserType] = useState<'usuario' | 'cliente'>('usuario')
  const [newUserRole, setNewUserRole] = useState<'admin' | 'rh'>('rh')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [addingUser, setAddingUser] = useState(false)
  const [companyIdForUsers, setCompanyIdForUsers] = useState<string | null>(() => selectedCompanyId ?? null)
  const [companyIdForNewClient, setCompanyIdForNewClient] = useState<string | null>(null)
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null)
  const [editUserType, setEditUserType] = useState<'usuario' | 'cliente'>('usuario')
  const [editUserRole, setEditUserRole] = useState<'admin' | 'rh'>('rh')
  const [savingEdit, setSavingEdit] = useState(false)
  const [removeUserConfirm, setRemoveUserConfirm] = useState<{ userId: string; email: string } | null>(null)
  
  // Tax config states
  const [taxConfig, setTaxConfig] = useState<TaxConfig | null>(null)
  const [savingTaxConfig, setSavingTaxConfig] = useState(false)

  const isAdminForCompany = me?.memberships?.some(
    (m) => m.companyId === selectedCompanyId && m.role === 'admin'
  )
  const adminCompanies = (me?.memberships ?? []).filter((m) => m.role === 'admin')

  useEffect(() => {
    if (selectedCompanyId && me?.memberships?.some((m) => m.companyId === selectedCompanyId && m.role === 'admin')) {
      setCompanyIdForUsers(selectedCompanyId)
    }
  }, [selectedCompanyId, me?.memberships])

  useEffect(() => {
    if (newUserType === 'cliente' && companyIdForUsers && !companyIdForNewClient) {
      setCompanyIdForNewClient(companyIdForUsers)
    }
    if (newUserType === 'usuario') setCompanyIdForNewClient(null)
  }, [newUserType, companyIdForUsers, companyIdForNewClient])

  const exportMyData = async () => {
    const blob = await apiFetch<unknown>('/me/export', { method: 'GET' })
    // API retorna JSON; aqui exibimos sucesso e deixamos o download para endpoint de funcionário específico.
    console.log(blob)
    toast.success('Exportação preparada')
  }

  const refresh = async () => {
    await refreshMe()
    toast.success('Atualizado')
  }

  const companySettingsQ = useQuery({
    queryKey: ['company-settings', selectedCompanyId],
    enabled: !!selectedCompanyId,
    queryFn: () => apiFetch<{ minWageBase: number }>(`/companies/${selectedCompanyId}/settings`)
  })

  const companyUsersQ = useQuery({
    queryKey: ['company-users', companyIdForUsers],
    enabled: !!companyIdForUsers && !!isAdminForCompany,
    queryFn: () => apiFetch<{ users: CompanyUser[] }>(`/companies/${companyIdForUsers}/users`)
  })

  const taxConfigQ = useQuery({
    queryKey: ['tax-config', selectedCompanyId],
    enabled: !!selectedCompanyId && !!isAdminForCompany,
    queryFn: () => apiFetch<TaxConfig>(`/companies/${selectedCompanyId}/tax-config`)
  })

  const companyUsers = companyUsersQ.data?.users ?? []

  const companyIdForAdd = newUserType === 'cliente' ? companyIdForNewClient : companyIdForUsers
  
  // Inicializa taxConfig quando os dados chegam
  useEffect(() => {
    if (taxConfigQ.data && !taxConfig) {
      setTaxConfig(taxConfigQ.data)
    }
  }, [taxConfigQ.data, taxConfig])

  const addUser = async () => {
    if (!companyIdForAdd) return
    const email = newUserEmail.trim()
    if (!email) {
      toast.error('Informe o e-mail do usuário')
      return
    }
    const password = newUserPassword.trim()
    setAddingUser(true)
    try {
      await apiFetch(`/companies/${companyIdForAdd}/users`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          userType: newUserType,
          role: newUserType === 'cliente' ? 'rh' : newUserRole,
          password: password || undefined
        })
      })
      toast.success('Usuário incluído na empresa')
      setNewUserEmail('')
      setNewUserPassword('')
      await qc.invalidateQueries({ queryKey: ['company-users', companyIdForAdd] })
      if (companyIdForUsers !== companyIdForAdd) await qc.invalidateQueries({ queryKey: ['company-users', companyIdForUsers] })
    } catch {
      // apiFetch já exibe toast de erro
    } finally {
      setAddingUser(false)
    }
  }

  const openRemoveUserConfirm = (userId: string, email: string) => {
    setRemoveUserConfirm({ userId, email })
  }

  const removeUser = async () => {
    if (!companyIdForUsers || !removeUserConfirm) return
    const { userId } = removeUserConfirm
    setRemoveUserConfirm(null)
    try {
      await apiFetch(`/companies/${companyIdForUsers}/users/${userId}`, { method: 'DELETE' })
      toast.success('Usuário removido')
      await qc.invalidateQueries({ queryKey: ['company-users', companyIdForUsers] })
    } catch {
      toast.error('Falha ao remover usuário')
    }
  }

  const startEditUser = (u: CompanyUser) => {
    setEditingUser(u)
    setEditUserType((u.userType ?? 'usuario') as 'usuario' | 'cliente')
    setEditUserRole(u.role)
  }

  const saveEditUser = async () => {
    if (!companyIdForUsers || !editingUser) return
    setSavingEdit(true)
    try {
      await apiFetch(`/companies/${companyIdForUsers}/users/${editingUser.userId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          userType: editUserType,
          role: editUserType === 'cliente' ? 'rh' : editUserRole
        })
      })
      toast.success('Usuário atualizado')
      setEditingUser(null)
      await qc.invalidateQueries({ queryKey: ['company-users', companyIdForUsers] })
    } catch {
      // apiFetch já exibe toast de erro
    } finally {
      setSavingEdit(false)
    }
  }

  useEffect(() => {
    if (companySettingsQ.data?.minWageBase != null) {
      setMinWageStr(String(companySettingsQ.data.minWageBase))
    }
  }, [companySettingsQ.data?.minWageBase])

  const saveCompanySettings = async () => {
    if (!selectedCompanyId) return
    const raw = String(minWageStr ?? '').trim()
    // remove separador de milhar e normaliza decimal
    const normalized = raw.replace(/\./g, '').replace(',', '.')
    const value = Number(normalized)
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Informe um valor válido para a base da insalubridade')
      return
    }
    await apiFetch(`/companies/${selectedCompanyId}/settings`, {
      method: 'PATCH',
      body: JSON.stringify({ minWageBase: value })
    })
    toast.success('Parâmetros atualizados')
    await companySettingsQ.refetch()
  }

  const saveTaxConfig = async () => {
    if (!selectedCompanyId || !taxConfig) return
    setSavingTaxConfig(true)
    try {
      await apiFetch(`/companies/${selectedCompanyId}/tax-config`, {
        method: 'PUT',
        body: JSON.stringify(taxConfig)
      })
      toast.success('Configuração tributária salva')
      await taxConfigQ.refetch()
    } catch {
      // apiFetch já exibe toast de erro
    } finally {
      setSavingTaxConfig(false)
    }
  }

  const parsePtBrNumber = (v: unknown): number | null => {
    if (v == null) return null
    if (typeof v === 'number') return Number.isFinite(v) ? v : null
    const s = String(v).trim()
    if (!s) return null
    const n = Number(s.replace(/\./g, '').replace(',', '.'))
    return Number.isFinite(n) ? n : null
  }

  const formatPtBrNumber = (n: number, scale: number) => {
    if (!Number.isFinite(n)) return ''
    return n.toLocaleString('pt-BR', {
      minimumFractionDigits: scale,
      maximumFractionDigits: scale
    })
  }

  const updateInssBracket = (index: number, field: keyof TaxBracket, value: number) => {
    if (!taxConfig) return
    if (!Number.isFinite(value)) return
    const cur = taxConfig.inssBrackets[index]?.[field]
    if (cur === value) return
    const updated = [...taxConfig.inssBrackets]
    updated[index] = { ...updated[index], [field]: value }
    setTaxConfig({ ...taxConfig, inssBrackets: updated })
  }

  const addInssBracket = () => {
    if (!taxConfig) return
    setTaxConfig({
      ...taxConfig,
      inssBrackets: [...taxConfig.inssBrackets, { from: 0, to: 0, rate: 0, deduction: 0 }]
    })
  }

  const removeInssBracket = (index: number) => {
    if (!taxConfig) return
    const updated = taxConfig.inssBrackets.filter((_, i) => i !== index)
    setTaxConfig({ ...taxConfig, inssBrackets: updated })
  }

  const updateIrrfBracket = (index: number, field: keyof TaxBracket, value: number) => {
    if (!taxConfig) return
    if (!Number.isFinite(value)) return
    const cur = taxConfig.irrfBrackets[index]?.[field]
    if (cur === value) return
    const updated = [...taxConfig.irrfBrackets]
    updated[index] = { ...updated[index], [field]: value }
    setTaxConfig({ ...taxConfig, irrfBrackets: updated })
  }

  const addIrrfBracket = () => {
    if (!taxConfig) return
    setTaxConfig({
      ...taxConfig,
      irrfBrackets: [...taxConfig.irrfBrackets, { from: 0, to: 0, rate: 0, deduction: 0 }]
    })
  }

  const removeIrrfBracket = (index: number) => {
    if (!taxConfig) return
    const updated = taxConfig.irrfBrackets.filter((_, i) => i !== index)
    setTaxConfig({ ...taxConfig, irrfBrackets: updated })
  }

  return (
    <div className="space-y-6">
      <ConfirmModal
        open={!!removeUserConfirm}
        title="Remover usuário"
        message={
          removeUserConfirm
            ? `Remover o usuário ${removeUserConfirm.email} do acesso a esta empresa?`
            : ''
        }
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={removeUser}
        onCancel={() => setRemoveUserConfirm(null)}
      />
      <div>
        <div className="text-xl font-semibold text-slate-900">Configurações</div>
        <div className="mt-1 text-sm text-slate-500">Preferências, LGPD e auditoria.</div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-sm font-semibold text-slate-900">Conta</div>
          <div className="mt-2 text-sm text-slate-700">{me?.email ?? ''}</div>
          <div className="mt-4 flex gap-2">
            <button
              className="h-10 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800"
              onClick={refresh}
              type="button"
            >
              Recarregar
            </button>
            <button
              className="h-10 rounded-xl bg-slate-100 px-4 text-sm font-semibold text-slate-800 hover:bg-slate-200"
              onClick={exportMyData}
              type="button"
            >
              Exportar meus dados (LGPD)
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-sm font-semibold text-slate-900">Empresa</div>
          {!selectedCompanyId ? (
            <div className="mt-2 text-sm text-slate-500">Selecione uma empresa no topo para configurar.</div>
          ) : (
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Base para cálculo da insalubridade (R$)</label>
                <IMaskInput
                  mask={Number}
                  radix=","
                  thousandsSeparator="."
                  scale={2}
                  padFractionalZeros
                  normalizeZeros
                  value={minWageStr ?? ''}
                  className="mt-1 h-11 w-full max-w-sm rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                  onAccept={(v) => setMinWageStr(String(v))}
                />
                <div className="mt-1 text-xs text-slate-500">
                  Este valor será usado como base da <span className="font-medium">insalubridade</span> no simulador de custo.
                </div>
              </div>

              <button
                type="button"
                className="h-10 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                onClick={saveCompanySettings}
                disabled={companySettingsQ.isLoading}
              >
                Salvar parâmetros
              </button>
            </div>
          )}
        </div>

        {selectedCompanyId && isAdminForCompany && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
            <div className="text-sm font-semibold text-slate-900">Gestão de usuários</div>
            <div className="mt-1 text-xs text-slate-500">
              Inclua ou remova usuários com acesso a esta empresa. Para cliente, escolha a empresa em que ele ficará vinculado (os dados serão armazenados nela). Se o usuário ainda não existir, informe uma senha para criar.
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
              <div className="md:col-span-3 lg:col-span-2">
                <label className="text-xs font-medium text-slate-600">E-mail do usuário</label>
                <input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  autoComplete="off"
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Tipo</label>
                <select
                  value={newUserType}
                  onChange={(e) => setNewUserType(e.target.value as 'usuario' | 'cliente')}
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                >
                  <option value="usuario">Usuário</option>
                  <option value="cliente">Cliente</option>
                </select>
              </div>
              {newUserType === 'usuario' && (
                <div>
                  <label className="text-xs font-medium text-slate-600">Perfil</label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'rh')}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  >
                    <option value="rh">RH</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
              )}
              {newUserType === 'cliente' && (
                <div className="md:col-span-3 lg:col-span-4">
                  <label className="text-xs font-medium text-slate-600">Empresa (onde o cliente será vinculado e os dados armazenados)</label>
                  <select
                    value={companyIdForNewClient ?? ''}
                    onChange={(e) => setCompanyIdForNewClient(e.target.value || null)}
                    className="mt-1 h-10 w-full max-w-md rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  >
                    <option value="">Selecione a empresa</option>
                    {adminCompanies.map((m) => (
                      <option key={m.companyId} value={m.companyId}>
                        {m.companyName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600">Senha (para criar)</label>
                <input
                  type="password"
                  placeholder="mín. 6 caracteres"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1 h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                />
              </div>
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="h-10 rounded-xl bg-[#616eb3] px-4 text-sm font-semibold text-white hover:bg-[#515a9e] disabled:opacity-60"
                onClick={addUser}
                disabled={addingUser || !companyIdForAdd}
              >
                {addingUser ? 'Incluindo…' : 'Incluir usuário'}
              </button>
            </div>
            {editingUser && (
              <div className="mt-4 rounded-xl border-2 border-[#616eb3]/30 bg-[#616eb3]/5 p-4">
                <div className="text-sm font-semibold text-slate-900">Editar usuário: {editingUser.email}</div>
                <div className="mt-3 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Tipo</label>
                    <select
                      value={editUserType}
                      onChange={(e) => setEditUserType(e.target.value as 'usuario' | 'cliente')}
                      className="mt-1 h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                    >
                      <option value="usuario">Usuário</option>
                      <option value="cliente">Cliente</option>
                    </select>
                  </div>
                  {editUserType === 'usuario' && (
                    <div>
                      <label className="text-xs font-medium text-slate-600">Perfil</label>
                      <select
                        value={editUserRole}
                        onChange={(e) => setEditUserRole(e.target.value as 'admin' | 'rh')}
                        className="mt-1 h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
                      >
                        <option value="rh">RH</option>
                        <option value="admin">Administrador</option>
                      </select>
                    </div>
                  )}
                  <button
                    type="button"
                    className="h-10 rounded-xl bg-[#616eb3] px-4 text-sm font-semibold text-white hover:bg-[#515a9e] disabled:opacity-60"
                    onClick={saveEditUser}
                    disabled={savingEdit}
                  >
                    {savingEdit ? 'Salvando…' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    className="h-10 rounded-xl bg-slate-200 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-300"
                    onClick={() => setEditingUser(null)}
                    disabled={savingEdit}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
            <div className="mt-4">
              <div className="text-xs font-medium text-slate-600">Usuários com acesso</div>
              {!companyIdForUsers && (
                <div className="mt-2 text-sm text-slate-500">Selecione uma empresa acima para listar e incluir usuários.</div>
              )}
              {companyIdForUsers && companyUsersQ.isLoading && (
                <div className="mt-2 text-sm text-slate-500">Carregando…</div>
              )}
              {companyIdForUsers && !companyUsersQ.isLoading && companyUsers.length === 0 && (
                <div className="mt-2 text-sm text-slate-500">Nenhum usuário além de você.</div>
              )}
              {companyIdForUsers && !companyUsersQ.isLoading && companyUsers.length > 0 && (
                <ul className="mt-2 space-y-2">
                  {companyUsers.map((u) => (
                    <li
                      key={u.userId}
                      className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="truncate text-sm font-medium text-slate-900">{u.email}</span>
                          {u.userType === 'cliente' && (
                            <span className="shrink-0 rounded-full bg-[#616eb3]/10 px-2 py-0.5 text-[11px] font-semibold text-[#616eb3]">
                              Cliente
                            </span>
                          )}
                          <span className="shrink-0 text-xs text-slate-500">
                            {u.role === 'admin' ? 'Administrador' : 'RH'}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          className="flex-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 sm:flex-initial"
                          onClick={() => startEditUser(u)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="flex-1 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 sm:flex-initial"
                          onClick={() => openRemoveUserConfirm(u.userId, u.email)}
                        >
                          Excluir
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Configuração Tributária (INSS e IRRF) */}
      {isAdminForCompany && selectedCompanyId && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">Configuração Tributária</div>
              <div className="mt-1 text-xs text-slate-500">
                Faixas de INSS e IRRF para cálculo do custo do funcionário
              </div>
            </div>
            <button
              type="button"
              className="h-10 rounded-xl bg-[#616eb3] px-4 text-sm font-semibold text-white hover:bg-[#515d9f] disabled:opacity-60"
              onClick={saveTaxConfig}
              disabled={savingTaxConfig || !taxConfig}
            >
              {savingTaxConfig ? 'Salvando...' : 'Salvar configuração'}
            </button>
          </div>

          {taxConfigQ.isLoading && (
            <div className="text-sm text-slate-500">Carregando configurações...</div>
          )}
          
          {taxConfigQ.isError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <div className="font-semibold">Erro ao buscar configuração tributária</div>
              <div className="mt-1 text-xs text-red-700">
                Tente novamente. Se persistir, a API retornará os valores padrão automaticamente.
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  className="h-9 rounded-lg bg-red-100 px-3 text-xs font-semibold text-red-800 hover:bg-red-200"
                  onClick={() => taxConfigQ.refetch()}
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {taxConfig && (
            <div className="space-y-6">
              {/* INSS */}
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">INSS - Faixas de Contribuição</div>
                
                <div className="space-y-3">
                  {taxConfig.inssBrackets.map((bracket, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-2 items-end gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-4 lg:grid-cols-[140px_140px_100px_140px_auto]"
                    >
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">De (R$)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={2}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.from, 2)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateInssBracket(idx, 'from', n)
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">Até (R$)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={2}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.to, 2)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateInssBracket(idx, 'to', n)
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">Alíquota (%)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={1}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.rate, 1)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateInssBracket(idx, 'rate', n)
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">Dedução (R$)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={2}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.deduction, 2)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateInssBracket(idx, 'deduction', n)
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeInssBracket(idx)}
                        className="h-8 w-8 shrink-0 rounded-lg bg-red-50 px-0 text-red-700 hover:bg-red-100"
                        title="Remover faixa"
                        aria-label="Remover faixa"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addInssBracket}
                  className="mt-3 h-9 rounded-lg bg-slate-100 px-3 text-xs font-medium text-slate-700 hover:bg-slate-200"
                >
                  + Adicionar faixa INSS
                </button>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="text-xs text-slate-600">Teto INSS Contribuinte (R$)</label>
                    <IMaskInput
                      mask={Number}
                      radix=","
                      mapToRadix={['.']}
                      thousandsSeparator="."
                      scale={2}
                      padFractionalZeros
                      normalizeZeros
                      unmask={false}
                      value={formatPtBrNumber(taxConfig.inssCeilingEmployee, 2)}
                      className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                      onAccept={(v) => {
                        const n = parsePtBrNumber(v)
                        if (n == null || n === taxConfig.inssCeilingEmployee) return
                        setTaxConfig({ ...taxConfig, inssCeilingEmployee: n })
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Teto INSS Empregador (R$)</label>
                    <IMaskInput
                      mask={Number}
                      radix=","
                      mapToRadix={['.']}
                      thousandsSeparator="."
                      scale={2}
                      padFractionalZeros
                      normalizeZeros
                      unmask={false}
                      value={formatPtBrNumber(taxConfig.inssCeilingEmployer, 2)}
                      className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                      onAccept={(v) => {
                        const n = parsePtBrNumber(v)
                        if (n == null || n === taxConfig.inssCeilingEmployer) return
                        setTaxConfig({ ...taxConfig, inssCeilingEmployer: n })
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Salário Família - Base (R$)</label>
                    <IMaskInput
                      mask={Number}
                      radix=","
                      mapToRadix={['.']}
                      thousandsSeparator="."
                      scale={2}
                      padFractionalZeros
                      normalizeZeros
                      unmask={false}
                      value={formatPtBrNumber(taxConfig.familyAllowanceBase, 2)}
                      className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                      onAccept={(v) => {
                        const n = parsePtBrNumber(v)
                        if (n == null || n === taxConfig.familyAllowanceBase) return
                        setTaxConfig({ ...taxConfig, familyAllowanceBase: n })
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Salário Família - Valor (R$)</label>
                    <IMaskInput
                      mask={Number}
                      radix=","
                      mapToRadix={['.']}
                      thousandsSeparator="."
                      scale={2}
                      padFractionalZeros
                      normalizeZeros
                      unmask={false}
                      value={formatPtBrNumber(taxConfig.familyAllowanceValue, 2)}
                      className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                      onAccept={(v) => {
                        const n = parsePtBrNumber(v)
                        if (n == null || n === taxConfig.familyAllowanceValue) return
                        setTaxConfig({ ...taxConfig, familyAllowanceValue: n })
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* IRRF */}
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">IRRF - Faixas de Tributação</div>
                
                <div className="space-y-3">
                  {taxConfig.irrfBrackets.map((bracket, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-2 items-end gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-4 lg:grid-cols-[140px_140px_100px_140px_auto]"
                    >
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">De (R$)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={2}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.from, 2)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateIrrfBracket(idx, 'from', n)
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">Até (R$)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={2}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.to, 2)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateIrrfBracket(idx, 'to', n)
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">Alíquota (%)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={1}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.rate, 1)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateIrrfBracket(idx, 'rate', n)
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="text-xs text-slate-600">Dedução (R$)</label>
                        <IMaskInput
                          mask={Number}
                          radix=","
                          mapToRadix={['.']}
                          thousandsSeparator="."
                          scale={2}
                          padFractionalZeros
                          normalizeZeros
                          unmask={false}
                          value={formatPtBrNumber(bracket.deduction, 2)}
                          className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                          onAccept={(v) => {
                            const n = parsePtBrNumber(v)
                            if (n == null) return
                            updateIrrfBracket(idx, 'deduction', n)
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeIrrfBracket(idx)}
                        className="h-8 w-8 shrink-0 rounded-lg bg-red-50 px-0 text-red-700 hover:bg-red-100"
                        title="Remover faixa"
                        aria-label="Remover faixa"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addIrrfBracket}
                  className="mt-3 h-9 rounded-lg bg-slate-100 px-3 text-xs font-medium text-slate-700 hover:bg-slate-200"
                >
                  + Adicionar faixa IRRF
                </button>

                <div className="mt-4">
                  <label className="text-xs text-slate-600">Valor por Dependente (R$)</label>
                  <IMaskInput
                    mask={Number}
                    radix=","
                    mapToRadix={['.']}
                    thousandsSeparator="."
                    scale={2}
                    padFractionalZeros
                    normalizeZeros
                    unmask={false}
                    value={formatPtBrNumber(taxConfig.irrfDependentDeduction, 2)}
                    className="mt-1 h-8 w-full max-w-xs rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900"
                    onAccept={(v) => {
                      const n = parsePtBrNumber(v)
                      if (n == null || n === taxConfig.irrfDependentDeduction) return
                      setTaxConfig({ ...taxConfig, irrfDependentDeduction: n })
                    }}
                  />
                </div>

                <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                  <div className="mb-3 text-sm font-semibold text-slate-900">IRRF - Redução por Faixa (2ª Tabela)</div>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!taxConfig.irrfReductionEnabled}
                      onChange={(e) => setTaxConfig({ ...taxConfig, irrfReductionEnabled: e.target.checked })}
                    />
                    Aplicar redução do IRRF conforme tabela adicional
                  </label>
                  <div className="mt-1 text-xs text-slate-500">
                    Até a faixa de isenção, a redução pode zerar o imposto. Na faixa intermediária, usa fórmula anual configurável.
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                      <label className="text-xs text-slate-600">Faixa isenta até (R$)</label>
                      <IMaskInput
                        mask={Number}
                        radix=","
                        mapToRadix={['.']}
                        thousandsSeparator="."
                        scale={2}
                        padFractionalZeros
                        normalizeZeros
                        unmask={false}
                        value={formatPtBrNumber(taxConfig.irrfReductionExemptLimit ?? 0, 2)}
                        className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                        disabled={!taxConfig.irrfReductionEnabled}
                        onAccept={(v) => {
                          const n = parsePtBrNumber(v)
                          if (n == null || n === taxConfig.irrfReductionExemptLimit) return
                          setTaxConfig({ ...taxConfig, irrfReductionExemptLimit: n })
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Redução máxima (R$)</label>
                      <IMaskInput
                        mask={Number}
                        radix=","
                        mapToRadix={['.']}
                        thousandsSeparator="."
                        scale={2}
                        padFractionalZeros
                        normalizeZeros
                        unmask={false}
                        value={formatPtBrNumber(taxConfig.irrfReductionMaxAmount ?? 0, 2)}
                        className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                        disabled={!taxConfig.irrfReductionEnabled}
                        onAccept={(v) => {
                          const n = parsePtBrNumber(v)
                          if (n == null || n === taxConfig.irrfReductionMaxAmount) return
                          setTaxConfig({ ...taxConfig, irrfReductionMaxAmount: n })
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Faixa da fórmula - De (R$)</label>
                      <IMaskInput
                        mask={Number}
                        radix=","
                        mapToRadix={['.']}
                        thousandsSeparator="."
                        scale={2}
                        padFractionalZeros
                        normalizeZeros
                        unmask={false}
                        value={formatPtBrNumber(taxConfig.irrfReductionRangeFrom ?? 0, 2)}
                        className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                        disabled={!taxConfig.irrfReductionEnabled}
                        onAccept={(v) => {
                          const n = parsePtBrNumber(v)
                          if (n == null || n === taxConfig.irrfReductionRangeFrom) return
                          setTaxConfig({ ...taxConfig, irrfReductionRangeFrom: n })
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Faixa da fórmula - Até (R$)</label>
                      <IMaskInput
                        mask={Number}
                        radix=","
                        mapToRadix={['.']}
                        thousandsSeparator="."
                        scale={2}
                        padFractionalZeros
                        normalizeZeros
                        unmask={false}
                        value={formatPtBrNumber(taxConfig.irrfReductionRangeTo ?? 0, 2)}
                        className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                        disabled={!taxConfig.irrfReductionEnabled}
                        onAccept={(v) => {
                          const n = parsePtBrNumber(v)
                          if (n == null || n === taxConfig.irrfReductionRangeTo) return
                          setTaxConfig({ ...taxConfig, irrfReductionRangeTo: n })
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Fórmula - Constante (R$)</label>
                      <IMaskInput
                        mask={Number}
                        radix=","
                        mapToRadix={['.']}
                        thousandsSeparator="."
                        scale={2}
                        padFractionalZeros
                        normalizeZeros
                        unmask={false}
                        value={formatPtBrNumber(taxConfig.irrfReductionFormulaBase ?? 0, 2)}
                        className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                        disabled={!taxConfig.irrfReductionEnabled}
                        onAccept={(v) => {
                          const n = parsePtBrNumber(v)
                          if (n == null || n === taxConfig.irrfReductionFormulaBase) return
                          setTaxConfig({ ...taxConfig, irrfReductionFormulaBase: n })
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Fórmula - Coeficiente</label>
                      <IMaskInput
                        mask={Number}
                        radix=","
                        mapToRadix={['.']}
                        thousandsSeparator="."
                        scale={6}
                        padFractionalZeros
                        normalizeZeros
                        unmask={false}
                        value={formatPtBrNumber(taxConfig.irrfReductionFormulaRate ?? 0, 6)}
                        className="mt-1 h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                        disabled={!taxConfig.irrfReductionEnabled}
                        onAccept={(v) => {
                          const n = parsePtBrNumber(v)
                          if (n == null || n === taxConfig.irrfReductionFormulaRate) return
                          setTaxConfig({ ...taxConfig, irrfReductionFormulaRate: n })
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

